import { useState, FormEvent } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

export default function ContactSection() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const header = useScrollReveal({ threshold: 0.1 });
  const infoReveal = useScrollReveal({ threshold: 0.1 });
  const formReveal = useScrollReveal({ threshold: 0.1 });

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch("https://readdy.ai/api/form/d7cjoi9noju8nk5a0t2g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="bg-black py-24 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div
          ref={header.ref}
          className={`text-center mb-16 transition-all duration-700 ${
            header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px w-12 bg-slate-400"></div>
            <span className="text-slate-300 text-sm font-semibold tracking-widest uppercase">
              Fale Conosco
            </span>
            <div className="h-px w-12 bg-slate-400"></div>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Reserve sua <span className="text-slate-300">Viagem</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Entre em contato e nossa equipe responderá em até 30 minutos.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-12">
          {/* Contact Info */}
          <div
            ref={infoReveal.ref}
            className={`lg:w-96 flex-shrink-0 transition-all duration-700 ${
              infoReveal.isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"
            }`}
          >
            <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-8 mb-6">
              <h3 className="text-white font-bold text-xl mb-6">Informações de Contato</h3>
              <div className="flex flex-col gap-5">
                {[
                  { icon: "ri-whatsapp-line", label: "WhatsApp", value: "(19) 97405-6980" },
                  { icon: "ri-mail-line", label: "E-mail&nbsp;", value: "Transportes.menezes@hotmail.com" },
                  { icon: "ri-mail-send-line", label: "E-mail Corporativo", value: "transportes.menezes@thnezes.com" },
                  { icon: "ri-map-pin-line", label: "Endereço", value: "Rua Cândido Bueno, 1299 - Sala 12 / Centro — Jaguariúna/SP · Jaguar Center Plaza" },
                  { icon: "ri-time-line", label: "Atendimento", value: "24 horas, 7 dias por semana" },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-slate-400/15 rounded-md flex-shrink-0">
                      <i className={`${item.icon} text-slate-300 text-lg`}></i>
                    </div>
                    <div>
                      <div className="text-gray-500 text-xs uppercase tracking-wide">{item.label}</div>
                      <div className="text-white text-sm font-medium mt-0.5">{item.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* WhatsApp CTA */}
            <a
              href="https://wa.me/5519974056980"
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="flex items-center justify-center gap-3 w-full bg-green-600 hover:bg-green-500 text-white font-bold py-4 rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-xl"></i>
              Chamar no WhatsApp
            </a>
          </div>

          {/* Form */}
          <div
            ref={formReveal.ref}
            className={`flex-1 transition-all duration-700 delay-150 ${
              formReveal.isVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-10"
            }`}
          >
            {submitted ? (
              <div className="bg-zinc-900 border border-slate-500/40 rounded-lg p-12 text-center h-full flex flex-col items-center justify-center">
                <div className="w-16 h-16 flex items-center justify-center bg-slate-400/20 rounded-full mb-4">
                  <i className="ri-check-line text-slate-300 text-3xl"></i>
                </div>
                <h3 className="text-white font-bold text-2xl mb-2">Mensagem Enviada!</h3>
                <p className="text-gray-400">Nossa equipe entrará em contato em breve. Obrigado!</p>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="bg-zinc-900 border border-zinc-800 rounded-lg p-8"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-gray-400 text-xs uppercase tracking-wide mb-2">Nome Completo *</label>
                    <input
                      type="text"
                      name="nome"
                      required
                      placeholder="Seu nome"
                      className="w-full bg-zinc-800 border border-zinc-700 focus:border-slate-400 text-white text-sm px-4 py-3 rounded-md outline-none transition-colors placeholder-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs uppercase tracking-wide mb-2">E-mail *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="seu@email.com"
                      className="w-full bg-zinc-800 border border-zinc-700 focus:border-slate-400 text-white text-sm px-4 py-3 rounded-md outline-none transition-colors placeholder-gray-600"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-gray-400 text-xs uppercase tracking-wide mb-2">Telefone / WhatsApp</label>
                    <input
                      type="tel"
                      name="telefone"
                      placeholder="(19) 99999-9999"
                      className="w-full bg-zinc-800 border border-zinc-700 focus:border-slate-400 text-white text-sm px-4 py-3 rounded-md outline-none transition-colors placeholder-gray-600"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs uppercase tracking-wide mb-2">Tipo de Serviço</label>
                    <select
                      name="servico"
                      className="w-full bg-zinc-800 border border-zinc-700 focus:border-slate-400 text-white text-sm px-4 py-3 rounded-md outline-none transition-colors cursor-pointer"
                    >
                      <option value="">Selecione...</option>
                      <option value="transfer-aeroporto">Transfer Aeroporto</option>
                      <option value="executivo-corporativo">Executivo Corporativo</option>
                      <option value="turismo">Turismo &amp; City Tour</option>
                      <option value="eventos">Eventos Especiais</option>
                      <option value="motorista-particular">Motorista Particular</option>
                      <option value="interestadual">Viagem Interestadual</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-gray-400 text-xs uppercase tracking-wide mb-2">Data da Viagem</label>
                    <input
                      type="date"
                      name="data"
                      className="w-full bg-zinc-800 border border-zinc-700 focus:border-slate-400 text-white text-sm px-4 py-3 rounded-md outline-none transition-colors cursor-pointer"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs uppercase tracking-wide mb-2">Horário</label>
                    <input
                      type="time"
                      name="horario"
                      className="w-full bg-zinc-800 border border-zinc-700 focus:border-slate-400 text-white text-sm px-4 py-3 rounded-md outline-none transition-colors cursor-pointer"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-gray-400 text-xs uppercase tracking-wide mb-2">Mensagem</label>
                  <textarea
                    name="mensagem"
                    rows={4}
                    maxLength={500}
                    placeholder="Descreva sua necessidade, origem, destino..."
                    className="w-full bg-zinc-800 border border-zinc-700 focus:border-slate-400 text-white text-sm px-4 py-3 rounded-md outline-none transition-colors resize-none placeholder-gray-600"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-slate-300 hover:bg-white disabled:opacity-60 text-black font-bold py-4 rounded-md transition-all duration-300 text-base flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line animate-spin"></i>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line"></i>
                      Solicitar Orçamento
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
